import React from 'react'
import { Modal, Typography } from 'antd'
import type { DiffLine } from './utils'

const { Text } = Typography

const CTX_WINDOW = 3

interface Props {
  open: boolean
  title: string
  lines: DiffLine[]
  onClose: () => void
}

export const DiffModal: React.FC<Props> = ({ open, title, lines, onClose }) => {
  const changed = lines.map((l) => l.type !== 'ctx')

  const show = lines.map((_, i) => {
    for (let d = -CTX_WINDOW; d <= CTX_WINDOW; d++) {
      if (changed[i + d]) return true
    }
    return false
  })

  const segments: Array<
    { kind: 'line'; line: DiffLine } | { kind: 'collapse'; count: number }
  > = []

  let i = 0
  while (i < lines.length) {
    if (!show[i]) {
      let j = i
      while (j < lines.length && !show[j]) j++
      segments.push({ kind: 'collapse', count: j - i })
      i = j
    } else {
      segments.push({ kind: 'line', line: lines[i] })
      i++
    }
  }

  const lineStyle = (type: DiffLine['type']): React.CSSProperties => ({
    display: 'flex',
    padding: '1px 16px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 1.55,
    background:
      type === 'ins' ? '#d1fae5' : type === 'del' ? '#fee2e2' : '#fff',
  })

  const prefixColor = (type: DiffLine['type']) =>
    type === 'ins' ? '#16a34a' : type === 'del' ? '#dc2626' : '#9ca3af'

  const prefixChar = (type: DiffLine['type']) =>
    type === 'ins' ? '+' : type === 'del' ? '-' : ' '

  return (
    <Modal
      open={open}
      title={
        <Text style={{ fontFamily: 'monospace', fontSize: 14 }}>{title}</Text>
      }
      onCancel={onClose}
      footer={null}
      width="92vw"
      style={{ maxWidth: 940 }}
      styles={{ body: { padding: 0, overflowY: 'auto', maxHeight: '70vh' } }}
    >
      {lines.length === 0 ? (
        <div
          style={{
            padding: 16,
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: 12,
          }}
        >
          No differences between these versions
        </div>
      ) : (
        segments.map((seg, idx) =>
          seg.kind === 'collapse' ? (
            <div
              key={idx}
              style={{
                padding: '4px 16px',
                color: '#9ca3af',
                fontSize: 11,
                background: '#f8fafc',
                borderTop: '1px solid #e2e5ea',
                borderBottom: '1px solid #e2e5ea',
                textAlign: 'center',
              }}
            >
              ··· {seg.count} unchanged line{seg.count === 1 ? '' : 's'} ···
            </div>
          ) : (
            <div key={idx} style={lineStyle(seg.line.type)}>
              <span
                style={{
                  width: 14,
                  flexShrink: 0,
                  userSelect: 'none',
                  color: prefixColor(seg.line.type),
                  fontWeight: seg.line.type !== 'ctx' ? 700 : undefined,
                }}
              >
                {prefixChar(seg.line.type)}
              </span>
              <span>{seg.line.text}</span>
            </div>
          ),
        )
      )}
    </Modal>
  )
}
